// =============================================================================
// UTM EPSG:25830 → WGS84 (EPSG:4326)
// Zone 30N, WGS84 ellipsoid
// =============================================================================

/**
 * Convert UTM EPSG:25830 (Zone 30N, WGS84) to WGS84 geographic coordinates.
 * Returns [longitude, latitude] in decimal degrees.
 *
 * Reference: "Map Projections — A Working Manual" (Snyder, 1987)
 */
export function utm25830ToLatLon(easting: number, northing: number): [number, number] {
  // WGS84 ellipsoid
  const a  = 6378137.0
  const f  = 1 / 298.257223563
  const e2 = 2 * f - f * f           // first eccentricity²
  const ep2 = e2 / (1 - e2)          // second eccentricity²

  const k0 = 0.9996                   // UTM scale factor
  const E0 = 500000                   // false easting (m)
  // Zone 30N central meridian: (30-1)*6 - 180 + 3 = -3°
  const lambda0 = -3 * (Math.PI / 180)

  // Series coefficient for footprint latitude
  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2))

  // Meridional arc
  const M  = northing / k0
  const mu = M / (a * (1 - e2 / 4 - 3 * e2 ** 2 / 64 - 5 * e2 ** 3 / 256))

  // Footprint latitude φ₁
  const phi1 = mu
    + (3 * e1 / 2 - 27 * e1 ** 3 / 32) * Math.sin(2 * mu)
    + (21 * e1 ** 2 / 16 - 55 * e1 ** 4 / 32) * Math.sin(4 * mu)
    + (151 * e1 ** 3 / 96) * Math.sin(6 * mu)
    + (1097 * e1 ** 4 / 512) * Math.sin(8 * mu)

  const sinPhi1 = Math.sin(phi1)
  const cosPhi1 = Math.cos(phi1)
  const tanPhi1 = Math.tan(phi1)

  const N1 = a / Math.sqrt(1 - e2 * sinPhi1 * sinPhi1)
  const T1 = tanPhi1 * tanPhi1
  const C1 = ep2 * cosPhi1 * cosPhi1
  const R1 = a * (1 - e2) / Math.pow(1 - e2 * sinPhi1 * sinPhi1, 1.5)
  const D  = (easting - E0) / (N1 * k0)

  // Latitude
  const lat = phi1 - (N1 * tanPhi1 / R1) * (
    D * D / 2
    - (5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * ep2) * D ** 4 / 24
    + (61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * ep2 - 3 * C1 * C1) * D ** 6 / 720
  )

  // Longitude
  const lon = lambda0 + (
    D
    - (1 + 2 * T1 + C1) * D ** 3 / 6
    + (5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * ep2 + 24 * T1 * T1) * D ** 5 / 120
  ) / cosPhi1

  return [
    lon * (180 / Math.PI),  // longitude
    lat * (180 / Math.PI),  // latitude
  ]
}
